import camelCase from 'lodash/camelCase';

import { REQUIRES_BY_PATH, leafGates, walkModules } from './modules';

/*
  Availability probe over the selected credit's feature entitlements
  (`credit_features`, keyed by config key -> { value }). Unlike the string
  `'1'`/`'0'` of an event's config entries above, `credit_features.value` is a
  JSON boolean, so disabled is the literal `false`. The api-service interceptor
  deep-camelCases the response (`camelcase-keys`), so keys are looked up by their
  camelCased form — lodash `camelCase` agrees for these config keys (locked by a
  spec). No credit (or an absent key) is treated as available.
*/
export const makeIsFeatureEnabled = credit => {
  const features = credit?.creditFeatures;
  if (!features) return () => true;
  return key => {
    const entry = features[camelCase(key)];
    return entry ? entry.value !== false : true;
  };
};

/*
  Resolve, for every payload leaf, its display state given the user's raw
  selection and the source event's config:

  - `available`: the source event has the feature, so there is something to copy.
  - `enabled`: the row is interactable — available, its parent module is on, and
    its dependency (if any) is satisfied.
  - `checked`: enabled and selected by the user.

  A disabled leaf reads as unchecked and serializes to `false`, which keeps the
  BE dependency rules (duplication_config.py) unreachable from the UI.
*/
export const computeLeafStates = (modules, selection, isConfigEnabled) => {
  const gates = leafGates(modules);

  const parentPathByPath = {};
  walkModules(modules, (node, ancestors) => {
    if (!node.path) return;
    const parent = ancestors[ancestors.length - 1];
    if (parent?.path) parentPathByPath[node.path] = parent.path;
  });

  const isAvailable = path => (gates[path] || []).every(key => isConfigEnabled(key));

  const selectedMemo = {};

  // `isSelected` and `isEnabled` share the same available/parent/dependency gate.
  // They're kept as two self-contained passes rather than one shared helper
  // because selected↔enabled is mutually recursive, which no-use-before-define
  // rejects; `isSelected` stays self-recursive so `isEnabled` can reuse it.
  function isSelected(path) {
    if (path in selectedMemo) return selectedMemo[path];
    selectedMemo[path] = false;
    let ok = isAvailable(path);
    const parentPath = parentPathByPath[path];
    if (ok && parentPath) ok = isSelected(parentPath);
    const requires = REQUIRES_BY_PATH[path];
    if (ok && requires) ok = isSelected(requires);
    ok = ok && !!selection[path];
    selectedMemo[path] = ok;
    return ok;
  }

  const isEnabled = path => {
    let ok = isAvailable(path);
    const parentPath = parentPathByPath[path];
    if (ok && parentPath) ok = isSelected(parentPath);
    const requires = REQUIRES_BY_PATH[path];
    if (ok && requires) ok = isSelected(requires);
    return ok;
  };

  const states = {};
  Object.keys(gates).forEach(path => {
    states[path] = {
      available: isAvailable(path),
      enabled: isEnabled(path),
      checked: isSelected(path),
    };
  });
  return states;
};

// The effective, dependency-honoring selection ready to serialize as the payload.
export const effectiveSelection = (modules, selection, isConfigEnabled) => {
  const states = computeLeafStates(modules, selection, isConfigEnabled);
  return Object.entries(states).reduce(
    (acc, [path, { checked }]) => ({ ...acc, [path]: checked }),
    {},
  );
};

// All descendant leaf paths of a node (used when toggling a parent/group row).
export const descendantPaths = node => {
  const paths = [];
  walkModules([node], child => {
    if (child.path) paths.push(child.path);
  });
  return paths;
};

// Count of `alwaysOn` descendants, which read as permanently checked members.
const alwaysOnCount = node => {
  let count = 0;
  walkModules([node], child => {
    if (child.alwaysOn) count += 1;
  });
  return count;
};

/*
  Aggregate checkbox state for a parent/group row from its descendants.
  `alwaysOn` descendants count as checked members, so a group keeps reading as
  selected even when every toggleable child is unavailable. With nothing left to
  toggle the row is disabled rather than an inert checkbox.
*/
export const groupState = (node, states) => {
  const enabledPaths = descendantPaths(node).filter(path => states[path]?.enabled);
  const checkedCount = enabledPaths.filter(path => states[path]?.checked).length;
  const alwaysOn = alwaysOnCount(node);
  const total = enabledPaths.length + alwaysOn;
  const checked = checkedCount + alwaysOn;
  return {
    disabled: enabledPaths.length === 0,
    checked: total > 0 && checked === total,
    indeterminate: checked > 0 && checked < total,
  };
};
